/**
 * 找回密码页面
 * 通过邮箱验证码重置密码
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authAPI } from '@/services/authApi';
import { Loader2, Mail, ArrowLeft, CheckCircle } from 'lucide-react';

type Step = 'email' | 'verify' | 'success';

export function ForgotPasswordPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeExpireSeconds, setCodeExpireSeconds] = useState(60);

  // 获取邮件服务配置
  useEffect(() => {
    authAPI.getEmailServiceStatus().then(res => {
      if (res.success && res.data) {
        setCodeExpireSeconds(res.data.codeExpireSeconds || 60);
      }
    }).catch(() => {
      // 如果获取失败，使用默认值
    });
  }, []);

  // 倒计时逻辑
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 第一步：发送验证码
  const handleSendCode = useCallback(async () => {
    if (!email) {
      setError('请输入邮箱');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('邮箱格式不正确');
      return;
    }

    setError('');
    setSendingCode(true);

    try {
      await authAPI.sendResetCode(email);
      setCountdown(codeExpireSeconds);
      setStep('verify');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : '发送验证码失败';
      setError(errorMessage);
    } finally {
      setSendingCode(false);
    }
  }, [email, codeExpireSeconds]);

  // 重新发送验证码
  const handleResendCode = useCallback(async () => {
    setError('');
    setSendingCode(true);

    try {
      await authAPI.sendResetCode(email);
      setCountdown(codeExpireSeconds);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : '发送验证码失败';
      setError(errorMessage);
    } finally {
      setSendingCode(false);
    }
  }, [email, codeExpireSeconds]);

  // 第二步：验证并重置密码
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!emailCode) {
      setError('请输入验证码');
      return;
    }

    if (!newPassword || newPassword.length < 8) {
      setError('新密码至少需要8个字符');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.resetPasswordByEmail(email, emailCode, newPassword);
      if (response.success) {
        setStep('success');
      } else {
        setError(response.message || '重置密码失败');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : '重置密码失败';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="flex-1 flex items-center justify-center w-full max-w-md">
        <Card className="w-full">
          {/* 第一步：输入邮箱 */}
          {step === 'email' && (
            <>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl font-bold text-center">找回密码</CardTitle>
                <CardDescription className="text-center">
                  输入您的注册邮箱，我们将发送验证码
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">注册邮箱</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={sendingCode}
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col space-y-4">
                <Button
                  className="w-full"
                  onClick={handleSendCode}
                  disabled={sendingCode}
                >
                  {sendingCode ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="mr-2 h-4 w-4" />
                  )}
                  发送验证码
                </Button>

                <Link
                  to="/login"
                  className="flex items-center justify-center text-sm text-gray-600 hover:text-blue-600 dark:text-gray-400"
                >
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  返回登录
                </Link>
              </CardFooter>
            </>
          )}

          {/* 第二步：输入验证码和新密码 */}
          {step === 'verify' && (
            <>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl font-bold text-center">重置密码</CardTitle>
                <CardDescription className="text-center">
                  验证码已发送至 {email}
                </CardDescription>
              </CardHeader>
              <form onSubmit={handleResetPassword}>
                <CardContent className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="emailCode">验证码</Label>
                    <div className="flex gap-2">
                      <Input
                        id="emailCode"
                        type="text"
                        placeholder="请输入6位验证码"
                        value={emailCode}
                        onChange={(e) => setEmailCode(e.target.value)}
                        required
                        disabled={loading}
                        maxLength={6}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleResendCode}
                        disabled={sendingCode || countdown > 0}
                        className="whitespace-nowrap min-w-[110px]"
                      >
                        {sendingCode ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : countdown > 0 ? (
                          `${countdown}秒后重发`
                        ) : (
                          '重新发送'
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword">新密码</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      placeholder="至少8位字符"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      disabled={loading}
                      minLength={8}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">确认新密码</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="再次输入新密码"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                </CardContent>

                <CardFooter className="flex flex-col space-y-4">
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    重置密码
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => { setStep('email'); setError(''); }}
                    className="text-sm"
                  >
                    <ArrowLeft className="mr-1 h-4 w-4" />
                    返回上一步
                  </Button>
                </CardFooter>
              </form>
            </>
          )}

          {/* 第三步：重置成功 */}
          {step === 'success' && (
            <>
              <CardHeader className="space-y-1">
                <div className="flex justify-center mb-4">
                  <CheckCircle className="h-16 w-16 text-green-500" />
                </div>
                <CardTitle className="text-2xl font-bold text-center">密码重置成功</CardTitle>
                <CardDescription className="text-center">
                  您的密码已成功重置，请使用新密码登录
                </CardDescription>
              </CardHeader>
              <CardFooter className="flex flex-col space-y-4">
                <Button
                  className="w-full"
                  onClick={() => navigate('/login')}
                >
                  前往登录
                </Button>
              </CardFooter>
            </>
          )}
        </Card>
      </div>

      {/* 备案信息 */}
      <footer className="py-4 text-center text-sm text-gray-500 dark:text-gray-400 space-y-1">
        <p>网站域名：yikaihui.com</p>
        <p>
          <a
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            京ICP备17037227号-1
          </a>
        </p>
      </footer>
    </div>
  );
}
